import { Icon, type IconNames } from '@petsmart-ui/sparky';
import type { MouseEvent } from 'react';

import { getBaseUrlJoined, trackNavigationHeaderClick } from '@libs/analytics';
import { useCart, useLogout } from '@libs/common-next';
import { handleAccountClick } from '@libs/experience-api';

import './links.scss';

type LinksProps = {
    onAction: () => void;
};

type NavigationClickInput = {
    event: MouseEvent<HTMLAnchorElement>;
    href: string;
    selection: string;
};

const SIGN_OUT_HREF = '/sign-in';

const NAV_LINKS = [
    { href: '/account/about-you', iconName: 'account-circle-outline', label: 'About you', selection: 'about you' },
    { href: '/account/order-history', iconName: 'box-outline', label: 'Orders', selection: 'orders' },
    { href: '/treats-rewards/authenticated/offers', iconName: 'treats-icon', label: 'Offers', selection: 'offers' },
    { href: '/account/auto-ship', iconName: 'autoship-outline', label: 'Autoship', selection: 'autoship' },
    {
        href: '/account/services-grooming',
        iconName: 'calendar-outline',
        label: 'Appointments',
        selection: 'appointments',
    },
    { href: '/account/address-book', iconName: 'address-outline', label: 'Addresses', selection: 'addresses' },
    { href: '/account/credit-cards', iconName: 'wallet-outline', label: 'Payments', selection: 'payments' },
    { href: '/account/favorites', iconName: 'heart-outline', label: 'Favorites', selection: 'favorites' },
];

function trackAccountNavClick(selection: string, href: string) {
    trackNavigationHeaderClick(selection, getBaseUrlJoined(href) ?? href);
}

export function Links({ onAction }: LinksProps) {
    const { clearCart } = useCart();
    const logout = useLogout();

    async function handleNavClick({ event, href, selection }: NavigationClickInput) {
        trackAccountNavClick(selection, href);
        onAction();
        await handleAccountClick({ event, href });
    }

    function handleLogout() {
        trackAccountNavClick('sign out', SIGN_OUT_HREF);
        onAction();
        logout({ redirectUrl: SIGN_OUT_HREF, forceRedirect: true, callback: clearCart });
    }

    return (
        <ul className="account-authenticated-links">
            {NAV_LINKS.map(link => (
                <li key={link.label}>
                    <a
                        href={link.href}
                        className="account-authenticated-links__link"
                        onClick={async event => {
                            await handleNavClick({ event, href: link.href, selection: link.selection });
                        }}
                    >
                        <Icon name={link.iconName as IconNames} size="sm" aria-hidden="true" />
                        {link.label}
                    </a>
                </li>
            ))}

            <li>
                <button type="button" className="account-authenticated-links__link" onClick={handleLogout}>
                    <Icon name="signout-outline" size="sm" aria-hidden="true" />
                    Sign out
                </button>
            </li>
        </ul>
    );
}
